/**
 * Typed gateway RPC errors preserved from wire ErrorShape.
 */

export type GatewayErrorShape = {
  code: string;
  message: string;
  details?: unknown;
  retryable?: boolean;
  retryAfterMs?: number;
};

export class GatewayRequestError extends Error {
  readonly code: string;
  readonly details?: unknown;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(shape: GatewayErrorShape) {
    super(shape.message);
    this.name = "GatewayRequestError";
    this.code = shape.code;
    this.details = shape.details;
    this.retryable = shape.retryable === true;
    this.retryAfterMs =
      typeof shape.retryAfterMs === "number" && shape.retryAfterMs > 0
        ? shape.retryAfterMs
        : undefined;
  }
}

export function isGatewayRequestError(err: unknown): err is GatewayRequestError {
  return err instanceof GatewayRequestError;
}

export function gatewayErrorFromShape(shape: GatewayErrorShape | undefined): GatewayRequestError {
  return new GatewayRequestError(
    shape ?? { code: "UNAVAILABLE", message: "request failed", retryable: true },
  );
}

/**
 * `chat.steer` returns NOT_STEERING when no embedded run is active or the run
 * is no longer streaming. This is a benign race (the run finished between the
 * GUI seeing it busy and the steer landing) — not a real failure. The caller
 * falls back to a fresh `chat.send` instead of surfacing an error banner.
 */
export function isSteerNotStreamingError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return msg.startsWith("NOT_STEERING");
}

/**
 * `chat.abort` with a specific `runId` returns NOT_FOUND when the gateway
 * no longer holds an AbortController for that run (already finished,
 * aborted, or the client latched a stale id). Treat as idempotent success.
 */
export function isAbortRunNotFoundError(err: unknown): boolean {
  if (isGatewayRequestError(err) && err.code === "NOT_FOUND") {
    return /runId not found|active chat controllers/i.test(err.message);
  }
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /runId not found in active chat controllers/i.test(msg);
}

export function isRetryableGatewayError(err: unknown): boolean {
  if (isGatewayRequestError(err)) {
    return err.retryable;
  }
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /429|rate.?limit|UNAVAILABLE|timed out|gateway timeout|All models failed/i.test(msg);
}

export function retryAfterMsFromError(err: unknown): number | undefined {
  if (isGatewayRequestError(err) && err.retryAfterMs) {
    return err.retryAfterMs;
  }
  const msg = err instanceof Error ? err.message : String(err ?? "");
  const m = /retry\s+(?:after\s+)?(\d+)\s*s/i.exec(msg);
  if (m) {
    return Math.max(1_000, Number(m[1]) * 1000);
  }
  if (/retry shortly|rate.?limit|429/i.test(msg)) {
    return 15_000;
  }
  return undefined;
}
